/**
 * The end of a session ([Review](../../../docs/REVIEW.md#session-recap)).
 *
 * Thirty seconds that turn *"I played a game"* into *"I learned five words"*.
 * Nothing plays automatically afterwards ([D-033](../../../docs/DECISIONS.md#d-033)),
 * so this **is** the ending rather than an interstitial.
 *
 * **"Stop for today" is the primary button, and that is the whole point.** It is
 * the opposite of what an engagement-optimised app does, and it is deliberate: a
 * clean, praised ending is what makes a child want to come back tomorrow
 * ([Product](../../../docs/PRODUCT.md#the-core-loop)). What comes next is shown
 * **by name and never as a button that starts it**, for the same reason.
 *
 * **No percentages below 100.** *"You got 14 right"*, never *"you got 78%"* — a
 * score out of a hundred is a mark, and this screen is not a report card
 * ([D-078](../../../docs/DECISIONS.md#d-078)).
 */

import '@capytalk/tokens/blocks/words.css';

import { useState } from 'react';
import Chest, { type Waiting } from './Chest.js';
import type { Client, Finished, Finishing } from './api.js';
import { assetUrl, say } from './audio.js';

type Recap = Finishing['recap'];
type Word = Recap['newWords'][number];

/**
 * The words this sitting was the first to teach.
 *
 * Tappable, because a word a child has just met is the one they are most likely
 * to want to hear again — and because the card is the same one the collection
 * uses, so the two screens teach the same gesture.
 */
function NewWords({ words }: { readonly words: readonly Word[] }) {
  const [saying, setSaying] = useState<string | null>(null);

  return (
    <div style={{ width: '100%', marginTop: 16, textAlign: 'left' }}>
      <h3>
        {words.length === 1
          ? 'You learned a new word'
          : `You learned ${String(words.length)} new words`}
      </h3>
      <div className="grid3" style={{ marginTop: 9 }}>
        {words.map((word) => (
          <button
            key={word.lexemeId}
            type="button"
            className="word"
            style={{ font: 'inherit', color: 'inherit', cursor: 'pointer' }}
            aria-label={`Hear ${word.word}`}
            onClick={() => {
              setSaying(word.lexemeId);
              void say(word.audioRef, word.word).finally(() => {
                setSaying(null);
              });
            }}
          >
            <div className="a" aria-hidden="true">
              {word.imageRef === null ? (
                '📖'
              ) : (
                <img
                  src={assetUrl(word.imageRef)}
                  alt=""
                  style={{ width: 30, height: 30, display: 'block', margin: '0 auto' }}
                />
              )}
            </div>
            <div className="w" aria-hidden="true">
              {word.word}
            </div>
            <div className="s" aria-hidden="true">
              {saying === word.lexemeId ? '🔊' : word.meaning}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/** What this sitting earned, and what the totals are now. */
export function Earned({
  payout,
  xpTotal,
}: {
  readonly payout: Finishing['payout'];
  readonly xpTotal: number;
}) {
  const stat = (value: string, label: string, colour: string) => (
    <div className="stat" key={label}>
      <b style={{ color: colour }}>{value}</b>
      <span className="tiny">{label}</span>
    </div>
  );

  return (
    <div style={{ width: '100%', marginTop: 16 }}>
      <div className="row" style={{ gap: 9 }}>
        {stat(`+${String(payout.xp)}`, 'points', 'var(--gold-ink)')}
        {stat(`+${String(payout.acorns)}`, 'acorns', 'var(--acorn-ink)')}
        {payout.bestCombo > 1
          ? stat(String(payout.bestCombo), 'in a row', 'var(--flame-ink)')
          : stat(String(payout.streak), 'days in a row', 'var(--green-ink)')}
      </div>
      {/*
        The running total, under what was just earned. A child who cannot yet
        hold "how much have I got altogether" in their head should not have to.
      */}
      {xpTotal > 0 && (
        <p className="tiny" style={{ marginTop: 8, textAlign: 'center' }}>
          {xpTotal} points altogether
        </p>
      )}
      {!payout.paid && (
        <p className="tiny" style={{ marginTop: 8, textAlign: 'center' }}>
          You have done this one before, so the acorns are already yours.
        </p>
      )}
    </div>
  );
}

/**
 * Any daily quest this sitting finished.
 *
 * Shown because it is otherwise paid in silence: quests have no claim button
 * ([D-077](../../../docs/DECISIONS.md#d-077)), so this is the only place a child
 * is told it happened at all.
 */
function Quests({ quests }: { readonly quests: Finishing['quests'] | undefined }) {
  // Absent from an older server's answer, for the same reason the recap is.
  const done = (quests?.quests ?? []).filter((quest) => quest.complete);
  if (done.length === 0) return null;

  return (
    <div className="card" style={{ marginTop: 12, width: '100%', textAlign: 'left' }}>
      <h3>{quests?.allThree === true ? 'All three quests done!' : 'Quest done'}</h3>
      {done.map((quest) => (
        <div className="tiny" key={quest.kind} style={{ marginTop: 4 }}>
          <span aria-hidden="true">✅</span> {quest.says}
        </div>
      ))}
    </div>
  );
}

/**
 * Anything this lesson put a chest on the shelf for.
 *
 * Here rather than on its own screen because this is where a child already is
 * when they earn one, and a chest that needs finding is a chest that sits
 * unopened. They are shown one at a time: two chests at once is two
 * celebrations competing, and the second one still exists after the first.
 */
function Chests({
  chests,
  client,
}: {
  readonly chests: readonly Waiting[] | undefined;
  readonly client: Client | null;
}) {
  // Opened here, so the card disappears as each one is dealt with.
  const [done, setDone] = useState<readonly string[]>([]);
  // Absent from an older server's answer, exactly like the recap (D-066).
  const left = (chests ?? []).filter((chest) => !done.includes(chest.id));
  const next = left[0];

  if (next === undefined || client === null) return null;

  return (
    <>
      <Chest
        key={next.id}
        chest={next}
        client={client}
        onDone={() => {
          setDone((was) => [...was, next.id]);
        }}
      />
      {/* So that going on without opening it is not losing it (D-182). */}
      <p className="tiny" style={{ marginTop: 6 }}>
        Not now? It waits for you on your Profile.
      </p>
    </>
  );
}

/**
 * The whole ending.
 *
 * `onLeave` is both buttons' destination, and deliberately so: neither of them
 * starts anything. The difference between them is which one a child is offered
 * first, and that difference is the feature.
 */
/** What a lesson closed, as the server says it (D-183). */
type Closed = NonNullable<Finished['finished']>;

/**
 * Bits of paper falling, for a party.
 *
 * Plain CSS, positioned by index so it draws the same every time, and stopped
 * entirely by reduce motion, like every animation in the app
 * ([Accessibility](../../../docs/ACCESSIBILITY.md)): a child who asked for
 * stillness sees the card without the paper.
 */
export function Confetti({ pieces }: { readonly pieces: number }) {
  const colours = ['#F2B33D', '#57A84B', '#4A90D9', '#E8843C', '#D96BA8', '#8C6BD9'];
  return (
    <div className="confetti" aria-hidden="true">
      {Array.from({ length: pieces }, (_, n) => (
        <i
          key={n}
          style={{
            left: `${String((n * 37) % 100)}%`,
            background: colours[n % colours.length],
            animationDelay: `${String(((n * 53) % 100) / 60)}s`,
            animationDuration: `${String(2.2 + ((n * 29) % 10) / 8)}s`,
            transform: `rotate(${String((n * 71) % 360)}deg)`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * A chapter, a book, or everything, finished (D-183).
 *
 * The owner asked for it to be festive. A chapter gets a card and some paper;
 * a book more; the last chapter of the last book the biggest party the app
 * throws, because that is months of work by a child.
 */
function Celebration({ closed }: { readonly closed: Closed }) {
  const { chapter, next } = closed;
  const heading =
    closed.kind === 'course'
      ? 'You finished every book!'
      : closed.kind === 'book'
        ? `Book ${String(chapter.book)} finished!`
        : 'Chapter finished!';
  const icon = closed.kind === 'course' ? '🏆' : closed.kind === 'book' ? '📚' : '🎉';
  return (
    <section
      className={`card celebrate ${closed.kind}`}
      aria-label={heading}
      style={{ marginBottom: 12 }}
    >
      <Confetti
        pieces={closed.kind === 'course' ? 60 : closed.kind === 'book' ? 36 : 22}
      />
      <div className="celebrate-icon" aria-hidden="true">
        {icon}
      </div>
      <h2>{heading}</h2>
      <p className="sub" style={{ marginTop: 6 }}>
        {closed.kind === 'course'
          ? 'Every chapter of every book. You did all of it. Wow!'
          : `You finished “${chapter.titleL1}”.`}
      </p>
      {next !== null && (
        <p className="tiny" style={{ marginTop: 8 }}>
          <span aria-hidden="true">⏭️</span> Next: Book {next.book}, Chapter{' '}
          {next.ordinal} · <b>{next.titleL1}</b>
        </p>
      )}
    </section>
  );
}

/**
 * A badge this lesson earned ([Achievements](../../../docs/ACHIEVEMENTS.md)).
 *
 * Above the chests and below what the lesson paid, because an achievement is
 * the rarest thing on this screen and the only one that is about *weeks* rather
 * than the last ten minutes. Empty on almost every lesson, which is what makes
 * it worth stopping for when it is not.
 *
 * Read defensively like everything else here: a server one deploy behind sends
 * no `unlocked` at all ([D-066](../../../docs/DECISIONS.md#d-066)).
 */
function Unlocked({
  unlocked,
}: {
  readonly unlocked:
    | readonly {
        readonly achievement: {
          readonly nameL1: string;
          readonly badge: string;
          readonly hint: string;
        };
        readonly titles: readonly string[];
      }[]
    | undefined;
}) {
  const got = unlocked ?? [];
  if (got.length === 0) return null;

  return (
    <section style={{ marginTop: 14 }}>
      {got.map((each) => (
        <div
          key={each.achievement.nameL1}
          className="card"
          role="status"
          style={{ marginTop: 8, background: '#FDF6E0' }}
        >
          <div className="row" style={{ gap: 12 }}>
            <span aria-hidden="true" style={{ fontSize: 38 }}>
              {each.achievement.badge}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800 }}>
                New badge: {each.achievement.nameL1}
              </div>
              <p className="tiny" style={{ marginTop: 2 }}>
                {each.achievement.hint}
              </p>
            </div>
          </div>
          {each.titles.length > 0 && (
            <p className="tiny" style={{ marginTop: 8 }}>
              You can wear the title{' '}
              <b>
                {each.titles
                  .map((id) => id.replace(/^title\./, '').replace(/_/g, ' '))
                  .join(', ')}
              </b>{' '}
              now.
            </p>
          )}
        </div>
      ))}
    </section>
  );
}

/**
 * One thing that improved ([D-165](../../../docs/DECISIONS.md#d-165), mockup 26).
 *
 * A word, never a percentage: something that was tricky before and right today,
 * or missed and then beaten in this sitting. Tapping it says the word, like
 * every word card in the app. Absent when nothing honestly improved.
 */
function Improved({ improved }: { readonly improved: NonNullable<Recap['improved']> }) {
  const line =
    improved.how === 'since-before'
      ? `“${improved.word}” was tricky before. Today you got it right!`
      : `You got “${improved.word}” right on the second try!`;
  return (
    <button
      type="button"
      className="card"
      aria-label={`${line} Hear ${improved.word}`}
      style={{
        width: '100%',
        marginTop: 11,
        textAlign: 'left',
        background: '#EDF6EA',
        borderColor: '#BFE0B6',
        font: 'inherit',
        cursor: 'pointer',
      }}
      onClick={() => {
        void say(improved.audioRef, improved.word);
      }}
    >
      <span className="row">
        <span style={{ fontSize: 24 }} aria-hidden="true">
          📈
        </span>
        <span
          style={{ flex: 1, fontSize: 14, fontWeight: 800, color: 'var(--green-ink)' }}
        >
          {line}
        </span>
      </span>
    </button>
  );
}

export default function RecapScreen({
  earned,
  onLeave,
  onNextChapter,
  client = null,
}: {
  readonly earned: Finishing | null;
  readonly onLeave: () => void;
  /**
   * Open the map on this chapter. *Keep going* uses it when the lesson closed a
   * chapter, so it goes to the next one rather than wherever the map decides
   * (D-183). Absent, it is `onLeave`.
   */
  readonly onNextChapter?: ((chapterId: string) => void) | undefined;
  /** Absent in the tests that only care about what the ending says. */
  readonly client?: Client | null;
}) {
  // A finish whose request never landed. The lesson is done and the answers are
  // stored; there is simply nothing to report, so the ending is a quiet one.
  if (earned === null) {
    return (
      <button className="btn" type="button" style={{ marginTop: 18 }} onClick={onLeave}>
        Stop for today
      </button>
    );
  }

  // Read defensively. A server one deploy behind this app sends a finish with no
  // `recap` and no `quests` at all — the types say that cannot happen, and it is
  // exactly what happens mid-rollout ([D-066](../../../docs/DECISIONS.md#d-066)).
  // Reaching into a missing one would blank the ending for everybody until the
  // deploy caught up.
  const recap: Partial<Recap> = earned.recap ?? {};
  const newWords = recap.newWords ?? [];
  // Absent from a server one deploy behind, and from practice (D-066).
  const closed = 'finished' in earned ? (earned.finished ?? null) : null;
  const next = closed?.next ?? null;

  return (
    <>
      {closed !== null && <Celebration closed={closed} />}
      <Earned payout={earned.payout} xpTotal={recap.xpTotal ?? 0} />
      {newWords.length > 0 && <NewWords words={newWords} />}
      {recap.improved != null && <Improved improved={recap.improved} />}
      <Quests quests={earned.quests} />
      <Unlocked unlocked={'lessonId' in earned ? earned.unlocked : undefined} />
      {/*
        A practice session earns chests too: a bronze one for emptying the pool,
        and the quest and streak chests of the day (D-161). Read defensively,
        because a server one deploy behind sends a practice finish without them.
      */}
      <Chests chests={'chests' in earned ? earned.chests : []} client={client} />

      {recap.next != null && (
        // A name, not a button. Knowing what is waiting is the part worth
        // having; a button that starts it is the autoplay D-033 refuses.
        <p className="tiny" style={{ marginTop: 14 }}>
          <span aria-hidden="true">⏭️</span> Next time: <b>{recap.next}</b>
        </p>
      )}

      <button className="btn" type="button" style={{ marginTop: 18 }} onClick={onLeave}>
        Stop for today
      </button>
      <button
        className="btn ghost"
        type="button"
        style={{ marginTop: 8 }}
        onClick={() => {
          // To the next chapter by name when this closed one; it opens the map
          // there and starts nothing (D-033).
          if (next !== null && onNextChapter !== undefined)
            onNextChapter(next.chapterId);
          else onLeave();
        }}
      >
        {next !== null ? 'On to the next chapter' : 'Keep going'}
      </button>
    </>
  );
}
